import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { SharedModule } from 'src/shared/shared.module';
import {
  CurriculumType,
  SubjectCreateOption,
  SubjectPreviewControlName,
  SubjectTypeOption,
} from 'src/app/services/models/subjects/subject.model';
import { SubjectManagementStatusBadgeComponent } from '../status-badge/subject-management-status-badge.component';

@Component({
  selector: 'app-subject-info-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SharedModule,
    SubjectManagementStatusBadgeComponent,
  ],
  templateUrl: './subject-info-form.component.html',
  styleUrl: './subject-info-form.component.scss',
})
export class SubjectInfoFormComponent {
  @Input({ required: true }) subjectForm!: FormGroup;
  @Input() isSaved = false;
  @Input() subjectApprovalStatusLabel: string | null = null;
  @Input() subjectApprovalStatus: string | null = null;
  @Input() academicTermOptions: string[] = [];
  @Input() yearOptions: string[] = [];
  @Input() subjectTypeOptions: SubjectTypeOption[] = [];
  @Input() subjectOptions: SubjectCreateOption[] = [];
  @Input() curriculumTypeOptions: CurriculumType[] = [];
  @Input() submitted = false;
  @Input() allowDelete = true;

  @Output() requestSave = new EventEmitter<void>();
  @Output() requestDelete = new EventEmitter<void>();

  hasSubjectError(controlName: string): boolean {
    const control = this.subjectForm.get(controlName);

    return control != null && control.invalid && (this.submitted || control.touched);
  }

  getCreditDescriptionErrorMessage(): string {
    const control = this.subjectForm.get('creditDescription');

    if (control?.hasError('pattern') ?? false) {
      return 'รูปแบบไม่ถูกต้อง เช่น 3(3-0-6)';
    }

    return 'กรุณากรอกคำอธิบายหน่วยกิต';
  }

  getSubjectPreviewValue(controlName: SubjectPreviewControlName): string {
    if (controlName === 'subjectType') {
      return this.getSelectedSubjectTypeLabel();
    }

    if (controlName === 'subjectCode') {
      return this.getSelectedSubjectCodeLabel();
    }

    const value = this.subjectForm.getRawValue()[controlName];

    return value == null || value === '' ? '-' : String(value);
  }

  isSubjectCodeDisabled(): boolean {
    return this.isSaved || this.getSelectedSubjectTypeId() == null;
  }

  private getSelectedSubjectTypeLabel(): string {
    const studyModeId = this.getSelectedSubjectTypeId();
    const option = this.subjectTypeOptions.find((item) => item.id === studyModeId);

    if (option?.label != null && option.label !== '') {
      return option.label;
    }

    const subjectType = this.subjectForm.getRawValue().subjectType as unknown;

    if (typeof subjectType === 'string') {
      const label = subjectType.trim();
      const studyModeId = Number(label);

      if (!Number.isFinite(studyModeId)) {
        return label !== '' ? label : '-';
      }
    }

    if (typeof subjectType === 'object' && subjectType !== null && 'label' in subjectType) {
      const label = (subjectType as { label?: unknown }).label;

      return typeof label === 'string' && label !== '' ? label : '-';
    }

    return '-';
  }

  private getSelectedSubjectCodeLabel(): string {
    const subjectId = Number(this.subjectForm.getRawValue().subjectCode);
    const option = Number.isFinite(subjectId)
      ? this.subjectOptions.find((item) => item.id === subjectId)
      : undefined;

    return option?.code != null && option.code !== '' ? option.code : '-';
  }

  private getSelectedSubjectTypeId(): number | null {
    const subjectType = this.subjectForm.getRawValue().subjectType as unknown;

    if (typeof subjectType === 'number' && Number.isFinite(subjectType)) {
      return subjectType;
    }

    if (typeof subjectType === 'string' && subjectType.trim() !== '') {
      const studyModeId = Number(subjectType);

      return Number.isFinite(studyModeId) ? studyModeId : null;
    }

    if (typeof subjectType === 'object' && subjectType !== null && 'id' in subjectType) {
      const studyModeId = Number((subjectType as { id?: unknown }).id);

      return Number.isFinite(studyModeId) ? studyModeId : null;
    }

    return null;
  }
}
